import React, { useEffect, useState, useRef, useCallback, FC, KeyboardEvent, SyntheticEvent } from 'react';
import { pathOr } from 'ramda';
import errorWrapper from '../../presentational/Error/ErrorWrapper';
import { TSearchInput, TSearchSuggestionResults } from './types';
import {
  createArrayOfSearchResultStrings,
  getSuggestionResults,
  getUrlSearchParam,
  shouldFocusSearchInput,
  shouldClearSearchInput,
  styleSuggestionResult,
  shouldRedirectToSearchResults,
  userEnteredOnlyWhiteSpace,
  redirectSearchResults,
  resetPageSizeParam,
} from '../../../util/searchHelper';
import { generateKey } from '../../../util/util';
import { subscribe, unsubscribe } from '../../../util/urlHelper';

import { defaultSearchFilter } from '../SearchResults/constants';

const SearchInput: FC<TSearchInput> = (props: TSearchInput) => {
  const searchInput: any = useRef(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [userSearch, setuserSearch] = useState<string>(getUrlSearchParam() || '');

  const {
    propSearchPlaceholder,
    propSearchSiteKey,
    propEndpoints,
    propSearchRedirect,
    propListenToFocusEvents,
  } = props;
  // eslint-disable-next-line react/destructuring-assignment
  const propSearchFilter = props.propSearchFilter || defaultSearchFilter;

  const focusSearchInput = useCallback((): void => {
    const shouldFocus = shouldFocusSearchInput();
    if (shouldFocus) {
      searchInput.current.focus();
    }
  }, [searchInput]);

  const clearSearchField = useCallback((): void => {
    setuserSearch('');
    setSuggestions([]);
  }, [setuserSearch, setSuggestions]);

  const clearSearchInput = useCallback((): void => {
    const shouldClear = shouldClearSearchInput();

    if (shouldClear) {
      clearSearchField();
    }
  }, [clearSearchField]);

  useEffect(() => {
    if (propListenToFocusEvents === '1') {
      subscribe(focusSearchInput);
      subscribe(clearSearchInput);
    }
    return () => {
      if (propListenToFocusEvents === '1') {
        unsubscribe(focusSearchInput);
        unsubscribe(clearSearchInput);
      }
    };
  }, []);

  const redirectFromUserSearch = useCallback((): void => {
    redirectSearchResults(propEndpoints, userSearch, propSearchFilter, propSearchRedirect);
  }, [redirectSearchResults, userSearch]);

  const onSetUserSearch = (search: string) => {
    setuserSearch(search);
    if (propSearchRedirect === '0') {
      resetPageSizeParam();
    }
  };

  const onClickSuggestionResult = useCallback(
    (result: string): void => {
      setSuggestions([]);
      onSetUserSearch(result);
      redirectSearchResults(propEndpoints, result, propSearchFilter, propSearchRedirect);
    },
    [redirectSearchResults]
  );

  const handleRedirect = useCallback((): void => {
    if (shouldRedirectToSearchResults(userSearch)) {
      setSuggestions([]);
      redirectFromUserSearch();
    }
  }, [setSuggestions, redirectFromUserSearch]);

  const onKeyPress = useCallback(
    (e: KeyboardEvent<HTMLElement>): void => {
      if (e.key === 'Enter') {
        handleRedirect();
      }
    },
    [setSuggestions, redirectFromUserSearch]
  );

  const handleUserTypeSearchField = async (e: SyntheticEvent<HTMLElement>) => {
    const search = pathOr('', ['target', 'value'], e);
    setuserSearch(search);
    if (search === '') {
      clearSearchField();
      setuserSearch(search);
      return;
    }
    const onResultsSearch = (res: TSearchSuggestionResults) => {
      const splitSearchResults = res.suggestions.length > 0 ? createArrayOfSearchResultStrings(res) : [];
      setSuggestions(splitSearchResults);
    };

    await getSuggestionResults(search, propSearchSiteKey, onResultsSearch);
    onSetUserSearch(search);
  };

  const onBlur = () => {
    if (userEnteredOnlyWhiteSpace(userSearch)) {
      setuserSearch('');
    }
  };

  return (
    <div className="search-input-field">
      <div className="search-input-field-container">
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          autoComplete="off"
          className="input-field"
          placeholder={propSearchPlaceholder}
          onKeyDown={onKeyPress}
          onChange={handleUserTypeSearchField}
          value={userSearch}
          spellCheck="false"
          type="search"
          ref={searchInput}
          onBlur={onBlur}
        />
        <i
          role="tab"
          aria-labelledby="search"
          tabIndex={0}
          className="icon-magnifying-glass"
          onKeyDown={onKeyPress}
          onClick={handleRedirect}
        />
      </div>
      {suggestions.length > 0 && (
        <div className="search-results-wrapper">
          <div className="search-results-list">
            {suggestions.map((result: string, index: number) => {
              return (
                // eslint-disable-next-line
                <div
                  key={generateKey(`${result}-${index}`)}
                  className="search-result"
                  onClick={() => onClickSuggestionResult(result)}
                >
                  {result.split(' ').map((res: string, i: number) => {
                    return (
                      <span key={generateKey(`${res}-${i}`)} className={`${styleSuggestionResult(res, userSearch)}`}>
                        {`${res} `}
                      </span>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default errorWrapper(SearchInput);
